import { ButtonHTMLAttributes, AnchorHTMLAttributes, forwardRef, ReactNode } from 'react'
import { cn } from '../../utils'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accentSoft'
export type ButtonSize = 'sm' | 'md'

interface BaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Bootstrap icon class (e.g. "bi bi-plus-lg") rendered before children */
  icon?: string
  /** Stretch to fill the available width */
  block?: boolean
  /** Accent-tinted active state (e.g. filter toggle when open) */
  active?: boolean
  children?: ReactNode
}

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { href?: undefined }

type ButtonAsAnchor = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { href: string }

export type ButtonProps = ButtonAsButton | ButtonAsAnchor

function classesFor(variant: ButtonVariant, size: ButtonSize, block: boolean, active: boolean, className?: string) {
  return cn(
    styles.button,
    styles[variant],
    size === 'sm' && styles.sm,
    block && styles.block,
    active && styles.active,
    className,
  )
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(
    { variant = 'secondary', size = 'md', icon, block = false, active = false, className, children, ...rest },
    ref,
  ) {
    const cls = classesFor(variant, size, block, active, className)
    const content = (
      <>
        {icon && <i className={icon} aria-hidden="true" />}
        {children}
      </>
    )

    if ('href' in rest && rest.href !== undefined) {
      const { href, ...anchorRest } = rest as ButtonAsAnchor
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={cls} {...anchorRest}>
          {content}
        </a>
      )
    }

    return (
      <button ref={ref as React.Ref<HTMLButtonElement>} className={cls} {...(rest as ButtonAsButton)}>
        {content}
      </button>
    )
  },
)

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Bootstrap icon class (e.g. "bi bi-trash") */
  icon: string
  /** Accessible label — required since there is no visible text */
  'aria-label': string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ variant = 'ghost', size = 'md', icon, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={cn(
          styles.button,
          styles[variant],
          styles.iconButton,
          size === 'sm' && styles.sm,
          className,
        )}
        {...rest}
      >
        <i className={icon} aria-hidden="true" />
      </button>
    )
  },
)
